// Single-date Jalali-calendar picker, backed by UI::DateFieldComponent.
// Adapted from GreenPay's date_picker_controller.js: same architecture and
// calendar math, restyled to this app's Tailwind tokens (see
// datetime_engine.js) instead of GreenPay's CSS custom properties.
//
// The calendar the admin sees and clicks through is always Jalali, but the
// hidden field that actually gets submitted always carries a plain
// Gregorian ISO date ("YYYY-MM-DD") — Rails' date columns parse that
// natively, and this app has no server-side Jalali parsing. Don't change
// datePickerFormatValue's default away from "gregorian" without adding
// that first.
import { Controller } from "@hotwired/stimulus"
import moment from "jalali-moment"
import { renderMonth, renderPickerOverlay, makeFooter, makeTodayBtn, makeHint, getCal } from "./datetime_engine"

export default class extends Controller {
  static targets = [ "trigger", "display", "clear", "popover", "calendar", "hiddenInput" ]

  static values = {
    min: String, // ISO gregorian "YYYY-MM-DD" or jalali "jYYYY-jMM-jDD"
    max: String,
    placeholder: { type: String, default: "انتخاب تاریخ" },
    inline: { type: Boolean, default: false },
    format: { type: String, default: "gregorian" }, // hidden field's value format
    calendar: { type: String, default: "jalali" }, // displayed calendar
  }

  static classes = [ "open", "filled" ]

  connect() {
    moment.locale("fa")

    this.cal = getCal(this.calendarValue)
    const today = this.cal.today()
    this.selected = this.hasHiddenInputTarget ? this.parseDateValue(this.hiddenInputTarget.value) : null
    this.viewYear = this.selected ? this.cal.parts(this.selected).y : today.y
    this.viewMonth = this.selected ? this.cal.parts(this.selected).m : today.m
    this.showingPicker = false

    this.onOutsideClick = (event) => {
      if (!this.element.contains(event.target)) this.hide()
    }

    if (this.selected) {
      this.paintSelected()
    } else if (this.hasDisplayTarget) {
      this.displayTarget.textContent = this.placeholderValue
    }

    if (this.inlineValue) this.render()
  }

  disconnect() {
    document.removeEventListener("pointerdown", this.onOutsideClick)
  }

  toggle() {
    this.isOpen() ? this.hide() : this.show()
  }

  clear(event) {
    event?.stopPropagation()
    this.selected = null

    if (this.hasDisplayTarget) {
      this.displayTarget.textContent = this.placeholderValue
      this.displayTarget.classList.add("text-neutral-400")
    }
    if (this.hasClearTarget) this.clearTarget.classList.add("hidden")
    if (this.hasTriggerTarget) this.triggerTarget.classList.remove(...this.resolveFilledClasses())
    if (this.hasHiddenInputTarget) this.hiddenInputTarget.value = ""

    this.dispatch("clear")
    this.render()
  }

  isOpen() {
    if (this.inlineValue) return true
    return this.hasPopoverTarget && !this.popoverTarget.classList.contains("hidden")
  }

  show() {
    if (!this.hasPopoverTarget) return
    this.popoverTarget.classList.remove("hidden")
    if (this.hasTriggerTarget) this.triggerTarget.classList.add(...this.resolveOpenClasses())
    this.render()
    setTimeout(() => document.addEventListener("pointerdown", this.onOutsideClick), 0)
  }

  hide() {
    if (this.inlineValue) return
    if (!this.hasPopoverTarget) return
    this.popoverTarget.classList.add("hidden")
    if (this.hasTriggerTarget) this.triggerTarget.classList.remove(...this.resolveOpenClasses())
    document.removeEventListener("pointerdown", this.onOutsideClick)
  }

  host() {
    // Inline: draw into the calendar target if present — rendering into the
    // controller element itself would wipe siblings like the hidden input.
    if (this.inlineValue) return this.hasCalendarTarget ? this.calendarTarget : this.element
    return this.hasCalendarTarget ? this.calendarTarget : this.popoverTarget
  }

  pick(dateMoment) {
    this.selected = dateMoment.clone()
    this.paintSelected()

    const gregorian = dateMoment.clone().locale("en").format("YYYY-MM-DD")
    const { y, m, d } = this.cal.parts(dateMoment)
    const jalaliIso = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`

    if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = this.formatValue === "jalali" ? jalaliIso : gregorian
    }

    this.dispatch("change", { detail: { date: dateMoment.clone(), iso: jalaliIso, gregorian } })

    this.inlineValue ? this.render() : this.hide()
  }

  paintSelected() {
    if (!this.selected) return

    const { y, m, d } = this.cal.parts(this.selected)
    if (this.hasDisplayTarget) {
      this.displayTarget.textContent = this.cal.fmt(y, m, d)
      this.displayTarget.classList.remove("text-neutral-400")
    }
    if (this.hasClearTarget) this.clearTarget.classList.remove("hidden")
    if (this.hasTriggerTarget) this.triggerTarget.classList.add(...this.resolveFilledClasses())
  }

  navigate(delta) {
    this.viewMonth += delta
    if (this.viewMonth > 12) { this.viewMonth = 1; this.viewYear++ }
    if (this.viewMonth < 1) { this.viewMonth = 12; this.viewYear-- }
    this.render(delta > 0 ? "animate-cal-slide-ltr" : "animate-cal-slide-rtl")
  }

  render(anim = "") {
    const host = this.host()
    if (!host) return

    if (this.showingPicker) {
      renderPickerOverlay(
        host,
        this.viewYear, this.viewMonth,
        (y, m) => { this.viewYear = y; this.viewMonth = m; this.showingPicker = false; this.render() },
        () => { this.showingPicker = false; this.render() },
        this.cal,
      )
      return
    }

    const minDate = this.parseDateValue(this.minValue)
    const maxDate = this.parseDateValue(this.maxValue)

    renderMonth(host, {
      year: this.viewYear,
      month: this.viewMonth,
      selected: this.selected,
      cal: this.cal,
      minDate, maxDate, anim,
      onDay: (m) => this.pick(m),
      onPrev: () => this.navigate(-1),
      onNext: () => this.navigate(1),
      onHeader: () => { this.showingPicker = true; this.render() },
    })

    const today = this.cal.today()
    host.appendChild(
      makeFooter(
        makeTodayBtn(() => { this.viewYear = today.y; this.viewMonth = today.m; this.pick(this.cal.now()) }),
        makeHint(this.cal.fmt(today.y, today.m, today.d)),
      ),
    )
  }

  /**
   * Parse a value like "2025-06-01" (gregorian) or "1404-03-11" (jalali)
   * into a moment.
   *
   * Once `moment.locale("fa")` is set (connect() does this on every
   * instance), plain `moment(val, "YYYY-MM-DD", true)` silently
   * misinterprets the digits as *jalali* — the non-prefixed tokens only
   * mean "gregorian" under the default locale. `moment.from(val, "en", ...)`
   * pins the source calendar explicitly and parses correctly regardless of
   * the current global locale (same reason CAL_GREGORIAN.make uses it).
   */
  parseDateValue(val) {
    if (!val) return null

    const gregorian = moment.from(val, "en", "YYYY-MM-DD")
    if (gregorian.isValid()) return gregorian

    const jalali = moment(val, "jYYYY-jMM-jDD", true)
    if (jalali.isValid()) return jalali

    return null
  }

  // Named resolve*Classes (not open/filledClasses) to avoid colliding with
  // the `openClasses`/`filledClasses` getters Stimulus itself defines from
  // `static classes` above.
  resolveOpenClasses() { return this.hasOpenClass ? this.openClasses : [ "border-brand-500", "ring-2", "ring-brand-500" ] }
  resolveFilledClasses() { return this.hasFilledClass ? this.filledClasses : [] }
}
